import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import api from '../lib/api';
import { useAuth } from '../context/AuthContext';
import VideoPlayer from '../components/VideoPlayer';
import CommentPanel from '../components/CommentPanel';
import TranscriptDownloads from '../components/TranscriptDownloads';

function fmtDate(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' });
}

export default function VideoReviewPage() {
  const { videoAssetId }    = useParams();
  const navigate            = useNavigate();
  const location            = useLocation();
  const [searchParams]      = useSearchParams();
  const { user }            = useAuth();
  const initialCommentId    = searchParams.get('comment');
  const seekRef             = useRef(null);
  const initialSeekDone     = useRef(false);

  const [videoAsset,           setVideoAsset]           = useState(null);
  const [comments,             setComments]             = useState([]);
  const [currentTimeSeconds,   setCurrentTimeSeconds]   = useState(0);
  const [activeCommentId,      setActiveCommentId]      = useState(initialCommentId || null);
  const [playerReady,          setPlayerReady]          = useState(false);
  const [loading,              setLoading]              = useState(true);
  const [error,                setError]                = useState('');
  const [archivedVersions,     setArchivedVersions]     = useState([]);
  const [deletingPermanent,    setDeletingPermanent]    = useState(false);
  const [deleteArchiveConfirm, setDeleteArchiveConfirm] = useState(false);
  const [transcription,        setTranscription]        = useState(null);
  const [txOpen,               setTxOpen]               = useState(false);

  const isAdmin = user?.roles?.some(r => ['Admin', 'Power User', 'Producer'].includes(r));
  // R-2 (Context.HeliconflowReviewUploadRestore.md): storage delete is an explicit Admin action.
  const canPermanentDelete = user?.roles?.includes('Admin');

  const fetchComments = useCallback(async () => {
    try {
      const res = await api.get(`/api/comments/${videoAssetId}`);
      setComments(res.data);
    } catch {
      // non-fatal
    }
  }, [videoAssetId]);

  useEffect(() => {
    // Reset all per-video state before loading the new one
    setVideoAsset(null);
    setComments([]);
    setArchivedVersions([]);
    setError('');
    setLoading(true);
    setPlayerReady(false);
    setActiveCommentId(initialCommentId || null);
    initialSeekDone.current = false;

    async function load() {
      try {
        const [assetRes] = await Promise.all([
          api.get(`/api/video-assets/${videoAssetId}`),
          fetchComments(),
        ]);
        const asset = assetRes.data;
        setVideoAsset(asset);

        // Load transcript in parallel, non-blocking
        api.get(`/api/transcriptions/video_asset/${videoAssetId}`)
          .then(r => setTranscription(r.data))
          .catch(() => {});

        // Fetch archived history for this production
        const prodParam = asset.liveProductionId
          ? `liveProductionId=${asset.liveProductionId}`
          : asset.aiProductionId
            ? `aiProductionId=${asset.aiProductionId}`
            : null;
        if (prodParam) {
          api.get(`/api/video-assets/archived?${prodParam}`)
            .then(r => setArchivedVersions(r.data))
            .catch(() => {});
        }
      } catch {
        setError('Failed to load video.');
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [videoAssetId, fetchComments]);

  // Deep-link: seek to the linked comment once player is ready and comments are loaded
  useEffect(() => {
    if (!initialCommentId || initialSeekDone.current || !playerReady || !comments.length) return;
    const target = comments.find(c => c.id === initialCommentId);
    if (target) {
      seekRef.current?.(Number(target.timestampSeconds));
      initialSeekDone.current = true;
    }
  }, [initialCommentId, playerReady, comments]);

  function handleSeekTo(secs) {
    seekRef.current?.(secs);
  }

  function handleBack() {
    if (location.state?.returnTo) {
      const fwdState = location.state.returnVideo ? { openVideo: location.state.returnVideo } : undefined;
      navigate(location.state.returnTo, fwdState ? { state: fwdState } : undefined);
      return;
    }
    if (videoAsset?.liveProductionId) {
      navigate(`/production/${videoAsset.liveProductionId}?stage=review`);
    } else if (videoAsset?.aiProductionId) {
      navigate(`/ai?card=${videoAsset.aiProductionId}`);
    } else {
      navigate(-1);
    }
  }

  const [qualTransitioning, setQualTransitioning] = useState(false);
  async function handleQualTransition(newStatus) {
    if (qualTransitioning || !videoAsset) return;
    const endpoint = videoAsset.liveProductionId
      ? `/api/live-production/${videoAsset.liveProductionId}/status`
      : videoAsset.aiProductionId
        ? `/api/ai-production/${videoAsset.aiProductionId}/status`
        : null;
    if (!endpoint) return;
    setQualTransitioning(true);
    try {
      await api.patch(endpoint, { status: newStatus, changedBy: user?.id });
      // Video is no longer in Qual — return without reopening the modal
      if (location.state?.returnTo) {
        navigate(location.state.returnTo);
      } else {
        navigate(-1);
      }
    } catch (err) {
      alert(err.response?.data?.error || 'Status change failed');
      setQualTransitioning(false);
    }
  }

  const [restoring, setRestoring] = useState(false);

  async function handleRestore() {
    const target = videoAsset?.liveProductionId ? 'Live Production' : 'AI Production';
    if (!window.confirm(`Restore this video as the current ${target} card? If there's already a current video, it will be archived.`)) return;
    setRestoring(true);
    try {
      await api.post(`/api/video-assets/${videoAssetId}/restore`);
      if (videoAsset?.liveProductionId) {
        navigate(`/production/${videoAsset.liveProductionId}?stage=review`);
      } else if (videoAsset?.aiProductionId) {
        navigate(`/ai?card=${videoAsset.aiProductionId}`);
      } else if (location.state?.returnTo) {
        navigate(location.state.returnTo);
      } else {
        navigate(-1);
      }
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to restore video.');
    } finally {
      setRestoring(false);
    }
  }

  async function handlePermanentDelete() {
    setDeletingPermanent(true);
    try {
      await api.delete(`/api/video-assets/${videoAssetId}/permanent`);
      setDeleteArchiveConfirm(false);
      // Go back to production or navigate to another archived version
      const others = archivedVersions.filter(v => v.id !== videoAssetId);
      if (others.length) {
        navigate(`/review/${others[0].id}`, { state: location.state });
      } else if (location.state?.returnTo) {
        navigate(location.state.returnTo);
      } else if (videoAsset?.liveProductionId) {
        navigate(`/production/${videoAsset.liveProductionId}?stage=review`);
      } else if (videoAsset?.aiProductionId) {
        navigate(`/ai?card=${videoAsset.aiProductionId}`);
      } else {
        navigate(-1);
      }
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to permanently delete video.');
    } finally {
      setDeletingPermanent(false);
    }
  }

  if (loading) {
    return (
      <div style={FULL_SCREEN_CENTER}>
        <span style={{ fontSize: 13, color: 'var(--tx-faint)' }}>Loading…</span>
      </div>
    );
  }

  if (error || !videoAsset) {
    return (
      <div style={{ ...FULL_SCREEN_CENTER, flexDirection: 'column', gap: 14 }}>
        <div style={{ fontSize: 13, color: 'var(--s-incomplete-t)' }}>{error || 'Video not found.'}</div>
        <button className="btn-ghost" onClick={() => navigate(-1)}>← Go back</button>
      </div>
    );
  }

  const scriptName =
    videoAsset.liveProduction?.script?.scriptName ||
    videoAsset.aiProduction?.script?.scriptName;

  const isArchived = !!videoAsset.isArchived;

  return (
    <div className="vrp-root" style={{ background: '#07070c', height: '100vh', overflow: 'hidden', display: 'flex', flexDirection: 'column', fontFamily: 'var(--font)', color: 'var(--tx-primary)' }}>
      <style>{VRP_RESPONSIVE_CSS}</style>

      {/* Top bar */}
      <div style={{
        height: 48, flexShrink: 0,
        display: 'flex', alignItems: 'center',
        padding: '0 16px', gap: 12,
        borderBottom: '1px solid rgba(255,255,255,0.06)',
        background: 'rgba(7,7,12,0.92)',
        backdropFilter: 'blur(8px)',
      }}>
        <button
          onClick={handleBack}
          className="btn-ghost"
          style={{ flexShrink: 0, fontSize: 13 }}
        >
          {location.state?.returnLabel === 'Video Qual Review'
            ? 'Close'
            : videoAsset.liveProductionId ? 'Back to Live Production'
            : videoAsset.aiProductionId   ? 'Back to AI Production'
            : 'Back'}
        </button>

        <span style={{ color: 'rgba(255,255,255,0.15)', fontSize: 16, flexShrink: 0 }}>|</span>

        <div style={{ fontSize: 12, color: 'var(--tx-faint)', display: 'flex', alignItems: 'center', gap: 6, minWidth: 0, flex: 1, overflow: 'hidden' }}>
          {videoAsset.client?.name && (
            <>
              <span style={{ color: 'var(--tx-secondary)', whiteSpace: 'nowrap' }}>{videoAsset.client.name}</span>
              <span style={{ opacity: 0.4 }}>›</span>
            </>
          )}
          {scriptName && (
            <>
              <span style={{ color: 'var(--tx-secondary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{scriptName}</span>
              <span style={{ opacity: 0.4 }}>›</span>
            </>
          )}
          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{videoAsset.fileName}</span>
        </div>

        {/* Qual-context actions: transition video status without bouncing back to the modal */}
        {location.state?.returnLabel === 'Video Qual Review' && (
          <>
            <button
              className="btn-ghost"
              style={{ flexShrink: 0, fontSize: 13, borderStyle: 'dashed', color: 'var(--tx-secondary)' }}
              onClick={() => handleQualTransition('Editing')}
              disabled={qualTransitioning}
            >
              Back to Editing
            </button>
            <button
              className="btn-primary"
              style={{ flexShrink: 0, fontSize: 13 }}
              onClick={() => handleQualTransition('Approved')}
              disabled={qualTransitioning}
            >
              Approve
            </button>
          </>
        )}

        {/* Archived version picker */}
        {archivedVersions.length > 0 && (
          <select
            value={isArchived ? videoAssetId : ''}
            onChange={e => { if (e.target.value) navigate(`/review/${e.target.value}`); }}
            style={{ background: 'var(--bg-input)', border: '1px solid var(--border-mid)', borderRadius: 6, padding: '4px 8px', fontSize: 11, color: 'var(--tx-secondary)', fontFamily: 'var(--font)', cursor: 'pointer', flexShrink: 0 }}
          >
            {!isArchived && <option value="">Current version</option>}
            {isArchived && <option value={videoAssetId}>Archived — {fmtDate(videoAsset.archivedAt)}</option>}
            {archivedVersions.filter(v => v.id !== videoAssetId).map(v => (
              <option key={v.id} value={v.id}>
                Archived — {fmtDate(v.archivedAt)} ({v.commentCount} comment{v.commentCount !== 1 ? 's' : ''})
              </option>
            ))}
          </select>
        )}
      </div>

      {/* Archived banner */}
      {isArchived && (
        <div style={{
          flexShrink: 0,
          background: 'rgba(245,158,11,0.1)',
          borderBottom: '1px solid rgba(245,158,11,0.25)',
          padding: '8px 16px',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        }}>
          <div style={{ fontSize: 12, color: '#f59e0b', display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontWeight: 700 }}>Archived version</span>
            <span style={{ color: 'rgba(245,158,11,0.65)' }}>· Comments are read-only · Archived {fmtDate(videoAsset.archivedAt)}</span>
          </div>
          {isAdmin && (
            <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
              <button
                onClick={handleRestore}
                disabled={restoring}
                style={{ background: 'rgba(72,190,110,0.15)', color: 'var(--s-approved-t)', border: '1px solid rgba(72,190,110,0.35)', borderRadius: 6, padding: '5px 12px', fontSize: 11, fontWeight: 600, cursor: restoring ? 'wait' : 'pointer', fontFamily: 'var(--font)' }}
              >
                {restoring ? 'Restoring…' : `↶ Back to ${videoAsset?.liveProductionId ? 'Live' : 'AI'} Production`}
              </button>
              {canPermanentDelete && <button
                onClick={() => setDeleteArchiveConfirm(true)}
                disabled={restoring}
                style={{ background: 'rgba(212,85,85,0.15)', color: 'var(--s-incomplete-t)', border: '1px solid rgba(212,85,85,0.35)', borderRadius: 6, padding: '5px 12px', fontSize: 11, fontWeight: 600, cursor: 'pointer', fontFamily: 'var(--font)' }}
              >
                Permanently Delete
              </button>}
            </div>
          )}
        </div>
      )}

      {/* Two-column body — stacks vertically below 768px (see VRP_RESPONSIVE_CSS). */}
      <div className="vrp-body" style={{ flex: 1, display: 'flex', minHeight: 0, overflow: 'hidden' }}>

        {/* Video column */}
        <div className="vrp-video" style={{
          flex: '0 0 75%', display: 'flex', flexDirection: 'column',
          justifyContent: 'center', background: '#07070c', padding: 24,
          minHeight: 0, overflow: 'hidden',
        }}>
          {(videoAsset.transcodingStatus === 'pending' || videoAsset.transcodingStatus === 'processing') ? (
            <div style={{
              display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
              gap: 12, padding: 40, color: 'rgba(255,255,255,0.85)', textAlign: 'center',
            }}>
              <div style={{ fontSize: 16, fontWeight: 600 }}>Preparing video for playback…</div>
              <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.6)', maxWidth: 420 }}>
                This file's codec isn't natively playable in browsers, so we're generating a compatible MP4 proxy in the background. Refresh the page in a minute or two.
              </div>
            </div>
          ) : (
            <VideoPlayer
              key={videoAssetId}
              src={videoAsset.playbackUrl || videoAsset.cloudfrontUrl}
              poster={videoAsset.thumbnailUrl || undefined}
              comments={comments}
              seekRef={seekRef}
              onTimeUpdate={setCurrentTimeSeconds}
              onCommentDotClick={setActiveCommentId}
              onPlayerReady={() => setPlayerReady(true)}
            />
          )}
        </div>

        {/* Comment + Transcript column */}
        <div className="vrp-side" style={{ flex: '0 0 25%', display: 'flex', flexDirection: 'column', minHeight: 0, overflow: 'hidden', borderLeft: '1px solid rgba(255,255,255,0.06)' }}>
          <CommentPanel
            key={videoAssetId}
            videoAssetId={videoAssetId}
            currentTimeSeconds={currentTimeSeconds}
            onSeekTo={handleSeekTo}
            comments={comments}
            onCommentsChange={fetchComments}
            activeCommentId={activeCommentId}
            allowComments={!isArchived}
          />

          {/* Transcript panel */}
          {transcription && (
            <div style={{ flexShrink: 0, borderTop: '1px solid rgba(255,255,255,0.06)', background: 'var(--bg-base)' }}>
              <button
                onClick={() => setTxOpen(v => !v)}
                style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 14px', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--tx-secondary)', fontSize: 12, fontFamily: 'var(--font)' }}
              >
                <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span>⊙</span>
                  <span style={{ fontWeight: 600 }}>Transcript</span>
                  {transcription.status !== 'complete' && (
                    <span style={{ fontSize: 10, color: transcription.status === 'failed' ? 'var(--s-incomplete-t)' : 'var(--violet)' }}>
                      {transcription.status}
                    </span>
                  )}
                </span>
                <span style={{ fontSize: 10 }}>{txOpen ? '▾' : '▸'}</span>
              </button>

              {txOpen && transcription.status === 'complete' && transcription.transcriptText && (
                <div style={{ borderTop: '1px solid rgba(255,255,255,0.05)', maxHeight: 260, overflowY: 'auto' }}>
                  {/* Action buttons — Copy · TXT · SRT · VTT (shared control) */}
                  <div style={{ padding: '8px 14px', borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                    <TranscriptDownloads
                      transcriptionId={transcription.id}
                      transcriptText={transcription.transcriptText}
                      baseName={videoAsset?.fileName}
                    />
                  </div>
                  {/* Transcript text with word-level timestamps */}
                  <div style={{ padding: '10px 14px', fontSize: 12, color: 'var(--tx-secondary)', lineHeight: 1.7 }}>
                    {(() => {
                      const items = transcription.transcriptJson?.transcripts?.[0]
                        ? null
                        : transcription.transcriptJson?.items?.filter(i => i.type === 'pronunciation');
                      if (!items?.length) {
                        return <span>{transcription.transcriptText}</span>;
                      }
                      return items.map((item, idx) => (
                        <span
                          key={idx}
                          onClick={() => item.start_time && handleSeekTo(parseFloat(item.start_time))}
                          style={{ cursor: item.start_time ? 'pointer' : 'default', borderRadius: 3, padding: '1px 2px', display: 'inline' }}
                          onMouseEnter={e => { if (item.start_time) e.currentTarget.style.background = 'rgba(139,109,255,0.2)'; }}
                          onMouseLeave={e => { e.currentTarget.style.background = ''; }}
                        >
                          {item.alternatives[0]?.content || ''}{' '}
                        </span>
                      ));
                    })()}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Permanent delete confirmation modal */}
      {deleteArchiveConfirm && (
        <div className="modal-overlay" onClick={() => setDeleteArchiveConfirm(false)}>
          <div className="vc-modal" style={{ width: 420 }} onClick={e => e.stopPropagation()}>
            <div className="vc-header">
              <div><div className="vc-title">Permanently Delete Archived Video?</div></div>
              <button className="vc-close" onClick={() => setDeleteArchiveConfirm(false)}>✕</button>
            </div>
            <div style={{ padding: '16px 28px', fontSize: 13, color: 'var(--tx-secondary)', lineHeight: 1.6 }}>
              This will permanently delete <strong style={{ color: 'var(--tx-primary)' }}>{videoAsset.fileName}</strong> and all its comments from storage. This cannot be undone.
            </div>
            <div className="vc-footer">
              <div />
              <div style={{ display: 'flex', gap: 8 }}>
                <button className="btn-ghost" onClick={() => setDeleteArchiveConfirm(false)}>Cancel</button>
                <button
                  onClick={handlePermanentDelete}
                  disabled={deletingPermanent}
                  style={{ background: 'rgba(212,85,85,0.15)', color: 'var(--s-incomplete-t)', border: '1px solid rgba(212,85,85,0.35)', borderRadius: 'var(--radius-sm)', padding: '9px 18px', fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'var(--font)' }}
                >
                  {deletingPermanent ? 'Deleting…' : 'Permanently Delete'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const FULL_SCREEN_CENTER = {
  background: '#07070c',
  minHeight: '100vh',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontFamily: 'var(--font)',
};

// Responsive overrides for narrow viewports (phones). Below 768px the two
// columns stack — video on top in a 16:9 box, comments fill the rest. Uses
// `100dvh` so the iOS keyboard doesn't push the comment input off-screen.
const VRP_RESPONSIVE_CSS = `
  @media (max-width: 768px) {
    .vrp-root { height: 100dvh !important; }
    .vrp-body { flex-direction: column !important; }
    .vrp-video {
      flex: 0 0 auto !important;
      width: 100% !important;
      height: 56.25vw !important;
      max-height: 45vh !important;
      min-height: 180px !important;
      padding: 8px !important;
      justify-content: flex-start !important;
    }
    .vrp-side {
      flex: 1 1 0 !important;
      min-width: 0 !important;
      min-height: 0 !important;
      border-left: none !important;
      border-top: 1px solid rgba(255,255,255,0.06) !important;
    }
  }
`;
